import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import { profileApi } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'

const EMPTY_PASSWORD_FORM = {
  current_password: '',
  password: '',
  password_confirmation: '',
}

export default function AdminProfile() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [form, setForm] = useState(EMPTY_PASSWORD_FORM)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setMessage('')
    setError('')

    try {
      await profileApi.updatePassword(form)
      setForm(EMPTY_PASSWORD_FORM)
      setMessage(t('admin.profile.passwordUpdated'))
    } catch (err) {
      const validationMessage = err.response?.data?.errors?.current_password?.[0]
        || err.response?.data?.errors?.password?.[0]
        || err.response?.data?.message
      setError(validationMessage || t('admin.profile.passwordUpdateFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-2 text-3xl font-bold text-primary">{t('admin.profile.title')}</h1>
      <p className="mb-8 text-text/60">{t('admin.profile.subtitle')}</p>

      <Card className="mb-6">
        <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.profile.accountInfo')}</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-text/50">{t('admin.profile.name')}</dt>
            <dd className="mt-1 text-sm font-medium text-text">{user?.name || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-text/50">{t('admin.profile.username')}</dt>
            <dd className="mt-1 text-sm font-medium text-text">{user?.username || '—'}</dd>
          </div>
        </dl>
      </Card>

      <Card>
        <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.profile.changePassword')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AdminFormField label={t('admin.profile.currentPassword')} htmlFor="profile-current-password">
            <input
              id="profile-current-password"
              type="password"
              value={form.current_password}
              onChange={(event) => setForm({ ...form, current_password: event.target.value })}
              className={adminInputClass}
              autoComplete="current-password"
              required
            />
          </AdminFormField>

          <AdminFormField label={t('admin.profile.newPassword')} htmlFor="profile-new-password">
            <input
              id="profile-new-password"
              type="password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              className={adminInputClass}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </AdminFormField>

          <AdminFormField label={t('admin.profile.confirmPassword')} htmlFor="profile-confirm-password">
            <input
              id="profile-confirm-password"
              type="password"
              value={form.password_confirmation}
              onChange={(event) => setForm({ ...form, password_confirmation: event.target.value })}
              className={adminInputClass}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </AdminFormField>

          <p className="text-xs text-text/50">{t('admin.users.passwordHint')}</p>

          {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <Button type="submit" disabled={saving}>
            {saving ? t('common.loading') : t('admin.profile.savePassword')}
          </Button>
        </form>
      </Card>
    </div>
  )
}
